import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import AppButton from '@/components/ui/AppButton';
import { toast } from '@/lib/swal';
import { Label } from '@/components/ui/label';
import { 
  Database, DownloadCloud, HardDrive, Archive, 
  FileSpreadsheet, FileJson, Search, User, 
  Clock, AlertTriangle, ShieldCheck, RefreshCcw,
  CheckCircle2, Loader2, Info, Sparkles,
  UploadCloud, FileUp, Layers, Settings2, Trash2
} from 'lucide-react';
import { formatDate, formatDateTime } from '@/lib/utils';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const BackupManager = () => {
    const [stats, setStats] = useState(null);
    const [loadingStats, setLoadingStats] = useState(false);
    const [userSearch, setUserSearch] = useState('');
    const [isDownloading, setIsDownloading] = useState(null); 

    // Estados para Importación
    const [importFile, setImportFile] = useState(null);
    const [isValidating, setIsValidating] = useState(false);
    const [validationSummary, setValidationSummary] = useState(null);
    const [importStrategy, setImportStrategy] = useState('update'); // 'insert' | 'update'
    const [isImporting, setIsImporting] = useState(false);
    const [importResult, setImportResult] = useState(null);

    useEffect(() => {
        fetchStats();
    }, []);

    const fetchStats = async () => {
        setLoadingStats(true);
        try {
            const res = await axios.get(`${API}/admin/backup/stats`);
            setStats(res.data);
        } catch (error) {
            console.error('Error al cargar stats de respaldo:', error);
        } finally {
            setLoadingStats(false);
        }
    };

    const handleDownload = async (type, identifier = '') => {
        setIsDownloading(type);
        try {
            let url = '';
            if (type === 'full') url = `${API}/admin/backup/full`;
            else if (type === 'user') url = `${API}/admin/backup/usuario/${identifier}`;
            else url = `${API}/admin/backup/collection/${type}`;

            toast.info('Generando respaldo, por favor espera...', { timer: 3000 });

            const response = await axios.get(url, {
                
                responseType: 'blob'
            });

            const downloadUrl = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = downloadUrl;
            
            const contentDisposition = response.headers['content-disposition'];
            let filename = `respaldo_${type}_${formatDate(new Date())}.zip`;
            if (contentDisposition) {
                const fileNameMatch = contentDisposition.match(/filename=(.+)/);
                if (fileNameMatch) filename = fileNameMatch[1];
            }
            
            link.setAttribute('download', filename);
            document.body.appendChild(link);
            link.click();
            link.remove();
            
            toast.success('Respaldo descargado correctamente');
        } catch (error) {
            const errorMsg = error.response?.data?.detail || 'Error al generar el respaldo';
            toast.error(errorMsg);
        } finally {
            setIsDownloading(null);
        }
    };

    const handleFileSelect = (e) => {
        const file = e.target.files[0];
        if (!file) return;
        setImportFile(file);
        setValidationSummary(null);
        setImportResult(null);
    };

    const handleValidate = async () => {
        if (!importFile) return;
        setIsValidating(true);
        try {
            const formData = new FormData();
            formData.append('file', importFile);
            const res = await axios.post(`${API}/admin/backup/validate`, formData, {
                headers: { 'Content-Type': 'multipart/form-data' },
            });
            setValidationSummary(res.data);
            toast.success('Archivo validado correctamente');
        } catch (error) {
            const errorMsg = error.response?.data?.message || 'Archivo incompatible o corrupto';
            toast.error(errorMsg);
        } finally {
            setIsValidating(false);
        }
    };

    const handleExecuteImport = async () => {
        if (!importFile || !validationSummary) return;
        
        setIsImporting(true);
        try {
            const formData = new FormData();
            formData.append('file', importFile);
            formData.append('strategy', importStrategy);

            const res = await axios.post(`${API}/admin/backup/import`, formData, {
                headers: { 'Content-Type': 'multipart/form-data' },
            });
            
            setImportResult(res.data);
            toast.success('Migración completada exitosamente');
            fetchStats(); // Actualizar métricas
        } catch (error) {
            const errorMsg = error.response?.data?.detail || 'Error crítico durante la importación';
            toast.error(errorMsg);
        } finally {
            setIsImporting(false);
        }
    };

    const collections = [
        { id: 'users', label: 'Usuarios', icon: User },
        { id: 'sorteos', label: 'Sorteos', icon: Database },
        { id: 'boletos', label: 'Boletos', icon: Archive },
        { id: 'movimientos_vendedor', label: 'Movimientos', icon: RefreshCcw },
        { id: 'ganadores', label: 'Ganadores', icon: CheckCircle2 },
        { id: 'campanas', label: 'Campañas', icon: Sparkles },
    ];

    return (
        <div className="space-y-8">
            {/* CABECERA Y RESUMEN */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <Card className="md:col-span-2 border-none shadow-xl bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 text-white overflow-hidden relative">
                    <div className="absolute top-0 right-0 p-8 opacity-10">
                        <Database size={160} />
                    </div>
                    <CardHeader>
                        <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-xl bg-orange-500 flex items-center justify-center shadow-lg shadow-orange-500/20">
                                <ShieldCheck className="text-white" />
                            </div>
                            <div>
                                <CardTitle className="text-2xl font-black italic tracking-tighter uppercase">Gestión de Datos & Migración</CardTitle>
                                <CardDescription className="text-slate-400 font-medium">Exportación consolidada e Importación estructurada</CardDescription>
                            </div>
                        </div>
                    </CardHeader>
                    <CardContent className="space-y-6 relative z-10">
                        <div className="flex flex-wrap gap-8 py-2">
                            <div className="space-y-1">
                                <p className="text-xs font-bold text-orange-500 uppercase tracking-widest">Información Real</p>
                                <p className="text-3xl font-black tabular-nums">{stats?.users || 0} <span className="text-sm font-medium text-slate-400">Usu.</span></p>
                            </div>
                            <div className="space-y-1">
                                <p className="text-xs font-bold text-orange-500 uppercase tracking-widest">Transacciones</p>
                                <p className="text-3xl font-black tabular-nums">{stats?.boletos || 0} <span className="text-sm font-medium text-slate-400">Bol.</span></p>
                            </div>
                            <div className="space-y-1">
                                <p className="text-xs font-bold text-orange-500 uppercase tracking-widest">Sorteos</p>
                                <p className="text-3xl font-black tabular-nums">{stats?.sorteos || 0} <span className="text-sm font-medium text-slate-400">Srt.</span></p>
                            </div>
                        </div>
                        <div className="pt-4 flex flex-wrap gap-4">
                            <AppButton 
                                variant="primary" 
                                className="px-8 shadow-xl shadow-orange-500/20"
                                onClick={() => handleDownload('full')}
                                loading={isDownloading === 'full'}
                                icon={DownloadCloud}
                            >
                                Generar Respaldo Total
                            </AppButton>
                            <Button 
                                variant="outline" 
                                onClick={fetchStats}
                                disabled={loadingStats}
                                className="bg-white/5 border-white/10 text-white hover:bg-white/10"
                            >
                                <RefreshCcw className={`w-4 h-4 mr-2 ${loadingStats ? 'animate-spin' : ''}`} />
                                Refrescar Métricas
                            </Button>
                        </div>
                    </CardContent>
                </Card>

                <Card className="border-none shadow-xl bg-slate-50 dark:bg-white/5">
                    <CardHeader>
                        <CardTitle className="text-lg font-bold flex items-center gap-2 text-slate-800 dark:text-slate-100 uppercase tracking-tighter">
                            <AlertTriangle className="text-orange-500 w-5 h-5" />
                            Protocolo de Seguridad
                        </CardTitle>
                    </CardHeader>
                    <CardContent className="text-xs text-muted-foreground space-y-4 leading-relaxed">
                        <div className="p-3 rounded-xl bg-orange-500/10 border border-orange-500/20 text-orange-600 dark:text-orange-400">
                             Este módulo permite la manipulación de registros históricos. Siempre valida el archivo antes de importar.
                        </div>
                        <p className="font-medium">Uso adecuado:</p>
                        <ul className="space-y-2 font-medium">
                            <li className="flex gap-2">
                                <div className="mt-1 w-1.5 h-1.5 rounded-full bg-orange-500 shrink-0"></div>
                                Migraciones entre ambientes (Dev/Prod)
                            </li>
                            <li className="flex gap-2">
                                <div className="mt-1 w-1.5 h-1.5 rounded-full bg-orange-500 shrink-0"></div>
                                Restauración de usuarios específicos
                            </li>
                            <li className="flex gap-2">
                                <div className="mt-1 w-1.5 h-1.5 rounded-full bg-orange-500 shrink-0"></div>
                                Sincronización de catálogos antiguos
                            </li>
                        </ul>
                    </CardContent>
                </Card>
            </div>

            {/* SECCIÓN DE EXPORTACIÓN */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <Card className="md:col-span-1 border-none shadow-xl border border-slate-100 dark:border-white/5">
                    <CardHeader>
                        <CardTitle className="text-xl font-black flex items-center gap-2">
                            <User className="text-orange-500" /> Individual
                        </CardTitle>
                        <CardDescription>Exporta perfil, órdenes y boletos</CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-4">
                        <div className="relative">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground w-4 h-4" />
                            <Input 
                                placeholder="Email, Cédula o ID..." 
                                className="pl-10 h-12 rounded-xl focus-visible:ring-orange-500 border-none bg-slate-100/50 dark:bg-white/5"
                                value={userSearch}
                                onChange={(e) => setUserSearch(e.target.value)}
                            />
                        </div>
                        <AppButton 
                            variant="secondary" 
                            className="w-full h-12 rounded-xl"
                            disabled={!userSearch || userSearch.length < 3}
                            onClick={() => handleDownload('user', userSearch)}
                            loading={isDownloading === 'user'}
                            icon={FileSpreadsheet}
                        >
                            Exportar Detalles de Usuario
                        </AppButton>
                    </CardContent>
                </Card>

                <Card className="md:col-span-2 border-none shadow-xl overflow-hidden border border-slate-100 dark:border-white/5">
                    <CardHeader className="bg-slate-50/50 dark:bg-white/5 border-b border-slate-100 dark:border-white/5 py-4">
                        <CardTitle className="text-xl font-black flex items-center gap-2 uppercase tracking-tighter italic">
                             <Archive className="text-orange-500" /> Exportación por Módulos
                        </CardTitle>
                    </CardHeader>
                    <CardContent className="p-6">
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                            {collections.map((coll) => (
                                <div 
                                    key={coll.id}
                                    className="p-4 rounded-2xl border border-slate-100 dark:border-white/5 hover:border-orange-500/30 hover:bg-orange-500/5 transition-all group relative"
                                >
                                    <div className="flex items-center gap-3">
                                        <div className="w-10 h-10 rounded-xl bg-slate-100 dark:bg-white/10 flex items-center justify-center group-hover:bg-orange-500 group-hover:text-white transition-colors">
                                            <coll.icon size={20} />
                                        </div>
                                        <div>
                                            <p className="font-bold text-sm tracking-tight">{coll.label}</p>
                                            <button 
                                                className="text-[10px] font-black uppercase text-orange-500 flex items-center gap-1 hover:underline disabled:opacity-50"
                                                onClick={() => handleDownload(coll.id)}
                                                disabled={isDownloading !== null}
                                            >
                                                {isDownloading === coll.id ? 'Descargando...' : 'Descargar'}
                                                <DownloadCloud size={10} />
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </CardContent>
                </Card>
            </div>

            {/* NUEVA SECCIÓN: IMPORTACIÓN / MIGRACIÓN */}
            <Card className="border-none shadow-2xl overflow-hidden relative border-2 border-orange-500/10">
                <div className="absolute top-0 right-0 p-8 opacity-5 pointer-events-none">
                    <UploadCloud size={140} />
                </div>
                
                <CardHeader className="bg-slate-900 text-white py-6">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-xl bg-white/10 flex items-center justify-center">
                                <FileUp className="text-orange-400" />
                            </div>
                            <div>
                                <CardTitle className="text-xl font-black uppercase italic tracking-tighter">Importación & Migración</CardTitle>
                                <CardDescription className="text-slate-400">Sube archivos JSON o ZIP compatibles con el sistema</CardDescription>
                            </div>
                        </div>
                        {importFile && (
                            <Button 
                                variant="ghost" 
                                size="sm" 
                                className="text-xs text-red-400 hover:text-red-300 hover:bg-white/10"
                                onClick={() => { setImportFile(null); setValidationSummary(null); setImportResult(null); }}
                            >
                                <Trash2 className="w-4 h-4 mr-2" /> Cancelar
                            </Button>
                        )}
                    </div>
                </CardHeader>
                
                <CardContent className="p-8">
                    {!importFile ? (
                        <div 
                            className="border-2 border-dashed border-slate-200 dark:border-white/10 rounded-[2rem] p-12 flex flex-col items-center justify-center text-center space-y-4 hover:bg-slate-50 dark:hover:bg-white/5 transition-colors cursor-pointer group"
                            onClick={() => document.getElementById('import-input').click()}
                        >
                            <input 
                                id="import-input" 
                                type="file" 
                                className="hidden" 
                                accept=".json,.zip"
                                onChange={handleFileSelect}
                            />
                            <div className="w-20 h-20 rounded-3xl bg-slate-100 dark:bg-white/10 flex items-center justify-center group-hover:scale-110 transition-transform shadow-inner">
                                <UploadCloud className="w-10 h-10 text-slate-400 group-hover:text-orange-500 transition-colors" />
                            </div>
                            <div>
                                <h4 className="text-lg font-black tracking-tight">Selecciona el archivo de respaldo</h4>
                                <p className="text-sm text-muted-foreground max-w-md mx-auto">
                                    Formatos soportados: <strong>.zip</strong> (paquete completo) o <strong>.json</strong> (módulos individuales o usuario).
                                </p>
                            </div>
                        </div>
                    ) : (
                        <div className="space-y-8 animate-in fade-in zoom-in-95 duration-300">
                            {/* ESTADO DEL ARCHIVO SELECCIONADO */}
                            <div className="flex flex-col md:flex-row md:items-center justify-between p-6 rounded-3xl bg-slate-100/50 dark:bg-white/5 border border-slate-100 dark:border-white/5 gap-6">
                                <div className="flex items-center gap-5">
                                    <div className="w-14 h-14 rounded-2xl bg-orange-500/10 flex items-center justify-center">
                                        <FileJson className="text-orange-500 w-8 h-8" />
                                    </div>
                                    <div>
                                        <h4 className="font-black text-lg tracking-tight leading-none mb-1">{importFile.name}</h4>
                                        <p className="text-xs font-bold text-muted-foreground">Tamaño: {(importFile.size / 1024).toFixed(2)} KB</p>
                                    </div>
                                </div>
                                <div className="flex gap-3">
                                    {!validationSummary ? (
                                        <AppButton 
                                            variant="primary" 
                                            onClick={handleValidate}
                                            loading={isValidating}
                                            icon={ShieldCheck}
                                            className="px-8 h-12 rounded-xl"
                                        >
                                            Validar Estructura
                                        </AppButton>
                                    ) : (
                                        <Badge className="bg-green-500 text-white h-12 px-6 rounded-xl flex items-center gap-2 border-none">
                                            <CheckCircle2 size={16} /> Estructura Válida
                                        </Badge>
                                    )}
                                </div>
                            </div>

                            {/* RESUMEN DE VALIDACIÓN */}
                            {validationSummary && (
                                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                                    <div className="space-y-6">
                                        <div className="flex items-center gap-2 text-xl font-black italic tracking-tighter uppercase text-slate-700 dark:text-slate-300">
                                            <Layers className="text-orange-500" />
                                            Entidades Detectadas
                                        </div>
                                        <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                                            {Object.entries(validationSummary.entities).map(([key, count]) => (
                                                <div key={key} className="p-4 rounded-2xl bg-slate-50 dark:bg-white/5 border border-slate-100 dark:border-white/5 shadow-sm">
                                                    <p className="text-[10px] font-black uppercase text-muted-foreground mb-1 tracking-widest">{key}</p>
                                                    <p className="text-2xl font-black tabular-nums">{count}</p>
                                                </div>
                                            ))}
                                        </div>
                                        <div className="flex items-start gap-3 p-4 rounded-2xl bg-blue-500/5 border border-blue-500/10 text-blue-600 dark:text-blue-400 text-xs leading-relaxed">
                                            <Info size={16} className="shrink-0 mt-0.5" />
                                            Detectamos un archivo de tipo <strong>{validationSummary.type}</strong>. Selecciona una estrategia de importación antes de proceder.
                                        </div>
                                    </div>

                                    <div className="space-y-6">
                                        <div className="flex items-center gap-2 text-xl font-black italic tracking-tighter uppercase text-slate-700 dark:text-slate-300">
                                            <Settings2 className="text-orange-500" />
                                            Estrategia de Migración
                                        </div>
                                        <div className="p-6 rounded-[2rem] bg-slate-50 dark:bg-white/5 border border-slate-100 dark:border-white/5 space-y-6">
                                            <div className="space-y-2">
                                                <Label className="text-xs font-bold uppercase tracking-widest ml-1">Conflicto de Datos Existentes</Label>
                                                <Select value={importStrategy} onValueChange={setImportStrategy}>
                                                    <SelectTrigger className="h-14 rounded-2xl bg-white dark:bg-slate-900 border-none shadow-sm focus:ring-2 focus:ring-orange-500">
                                                        <SelectValue placeholder="Selecciona estrategia" />
                                                    </SelectTrigger>
                                                    <SelectContent className="rounded-2xl border-none shadow-2xl">
                                                        <SelectItem value="insert" className="rounded-xl py-3">
                                                            <div className="font-bold">Solo Insertar Nuevos</div>
                                                            <div className="text-[10px] opacity-70">Ignora registros con IDs que ya existen en la DB.</div>
                                                        </SelectItem>
                                                        <SelectItem value="update" className="rounded-xl py-3">
                                                            <div className="font-bold">Actualizar Existentes (Recomendado)</div>
                                                            <div className="text-[10px] opacity-70">Sobrescribe los datos si el ID coincide. Crea si no existe.</div>
                                                        </SelectItem>
                                                    </SelectContent>
                                                </Select>
                                            </div>

                                            {!importResult ? (
                                                <AppButton 
                                                    variant="primary" 
                                                    className="w-full h-14 rounded-2xl shadow-xl shadow-orange-500/20 text-md"
                                                    disabled={!validationSummary || isImporting}
                                                    onClick={handleExecuteImport}
                                                    loading={isImporting}
                                                    icon={UploadCloud}
                                                >
                                                    Confirmar e Importar Ahora
                                                </AppButton>
                                            ) : (
                                                <div className="bg-green-500/10 border border-green-500/20 p-4 rounded-2xl space-y-2">
                                                    <p className="font-black text-green-600 dark:text-green-400 flex items-center gap-2">
                                                        <CheckCircle2 size={16} /> Importación Completada
                                                    </p>
                                                    <div className="text-[10px] grid grid-cols-3 gap-2 text-muted-foreground font-bold">
                                                        <div>CREADOS: {importResult.created}</div>
                                                        <div>UPDATED: {importResult.updated}</div>
                                                        <div>ERRORES: {importResult.errors}</div>
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    )}
                </CardContent>
            </Card>
            
            {/* PIE DE PÁGINA TECNICO */}
            <div className="flex items-center justify-between px-6 py-4 bg-slate-100/50 dark:bg-white/5 rounded-2xl border border-slate-100 dark:border-white/5">
                <div className="flex items-center gap-4 text-[10px] font-black uppercase text-muted-foreground tracking-widest">
                    <div className="flex items-center gap-1.5">
                        <div className="w-2 h-2 rounded-full bg-green-500 animate-pulse"></div>
                        Núcleo de Migración Activo
                    </div>
                    <span className="opacity-20 text-lg">|</span>
                    <div className="flex items-center gap-1.5">
                        <FileJson size={14} className="text-orange-500" />
                        Compatibilidad nativa con Formatos Oficiales
                    </div>
                </div>
                <div className="text-[10px] font-medium text-slate-500 flex items-center gap-2 italic">
                    <Clock size={12} /> Sync: {formatDateTime(new Date())}
                </div>
            </div>
        </div>
    );
};

export default BackupManager;
